import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import moment from 'moment'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import {
  TaskFormsDataCollectionDocument,
  type TaskFormItemFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useActivities } from 'modules/core/lib/activities'
import {
  dateBackendDateTimeToRelativeTime,
  i18nDateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useTaskFormOne } from './one'

const TaskFormsContent = () => {
  const FORM_CATEGORY_LABELS: Record<string, string> = {
    basic: i18n.t('procurement.purchase_requests.settings.task_forms.form_category_value.basic'),
    provider: i18n.t(
      'procurement.purchase_requests.settings.task_forms.form_category_value.provider'
    ),
  }
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const { openActivity } = useActivities()

  const { handleCreateTaskForm } = useTaskFormOne({
    onFallback: () => openActivity('procurement.createTaskForm'),
  })

  const isDateOrRange = (value: unknown): value is DateRange | Date =>
    value instanceof Date || (value !== null && typeof value === 'object' && 'from' in value)

  const getDataFilterAsRange = (
    assessor: DateRange | Date | undefined
  ): { between: [string, string] } | undefined => {
    if (!assessor) return undefined

    let startMoment: moment.Moment
    let endMoment: moment.Moment

    if (assessor instanceof Date) {
      startMoment = moment(assessor).startOf('day')
      endMoment = moment(assessor).endOf('day')
    } else {
      startMoment = assessor.from ? moment(assessor.from).startOf('day') : moment().startOf('day')
      endMoment = assessor.to ? moment(assessor.to).endOf('day') : moment().endOf('day')
    }

    return {
      between: [
        i18nDateToString(startMoment, 'backend_datetime'),
        i18nDateToString(endMoment, 'backend_datetime'),
      ],
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: TaskFormsDataCollectionDocument,
    mapData: (data) => data.procurement.taskFormsConnection.edges.map((edge) => edge.node) ?? [],
    mapPagination: (data) => ({
      total: data.procurement.taskFormsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, filters, search }) => ({
      companyId: company?.id,
      sortOrder: sortings?.[0],
      search: search ?? undefined,
      formStatus: filters.formStatus?.[0],
      filter: {
        createdAt: getDataFilterAsRange(
          isDateOrRange(filters.createdAt) ? filters.createdAt : undefined
        ),
        updatedAt: getDataFilterAsRange(
          isDateOrRange(filters.updatedAt) ? filters.updatedAt : undefined
        ),
      },
    }),
    primaryActions: () => ({
      label: i18n.t('procurement.purchase_requests.settings.task_forms.actions.new'),
      icon: Plus,
      variant: 'default',
      onClick: handleCreateTaskForm,
    }),
    itemUrl: (item: TaskFormItemFragment) =>
      navigation.settings.procurement.taskForms.id.edit.fullPath({ id: item.id }),
    itemActions: (item: TaskFormItemFragment) => [
      {
        label: i18n.t('procurement.purchase_requests.settings.task_forms.actions.duplicate'),
        type: 'other' as const,
        onClick: () =>
          openActivity('procurement.duplicateTaskForm', {
            taskFormId: item.id,
            taskFormName: item.name,
          }),
      },
    ],
    search: { enabled: true },
    filters: {
      formStatus: {
        label: i18n.t('procurement.purchase_requests.settings.task_forms.columns.status'),
        type: 'in' as const,
        options: {
          options: [
            {
              value: 'active',
              label: i18n.t('forms.custom_templates.data_collection.status_values.published'),
            },
            {
              value: 'draft',
              label: i18n.t('forms.custom_templates.data_collection.status_values.draft'),
            },
          ],
        },
      },
      updatedAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.filters.updated_at'),
        type: 'date',
        options: {
          mode: 'range',
          view: 'day',
        },
      },
      createdAt: {
        label: i18n.t('procurement.purchase_requests.settings.data_collection.filters.created_at'),
        type: 'date',
        options: {
          mode: 'range',
          view: 'day',
        },
      },
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('procurement.purchase_requests.settings.task_forms.columns.name'),
                  render: (item: TaskFormItemFragment) => ({
                    type: 'text' as const,
                    value: {
                      text: item.name,
                    },
                  }),
                },
                {
                  label: i18n.t('procurement.purchase_requests.settings.task_forms.columns.status'),
                  render: (item: TaskFormItemFragment) => {
                    const status = item.formsConnection?.edges?.[0]?.node?.status
                    const label =
                      status === 'active'
                        ? i18n.t('forms.custom_templates.data_collection.status_values.published')
                        : status === 'draft'
                          ? i18n.t('forms.custom_templates.data_collection.status_values.draft')
                          : ''
                    return {
                      type: 'status' as const,
                      value: {
                        label,
                        status: status === 'active' ? 'positive' : 'neutral',
                      },
                    }
                  },
                },
                {
                  label: i18n.t(
                    'procurement.purchase_requests.settings.task_forms.columns.form_category'
                  ),
                  render: (item: TaskFormItemFragment) => ({
                    type: 'text' as const,
                    value: {
                      text: (item.category && FORM_CATEGORY_LABELS[item.category]) ?? '',
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'procurement.purchase_requests.settings.task_forms.columns.created'
                  ),
                  render: (item: TaskFormItemFragment) => ({
                    type: 'text' as const,
                    value: {
                      text: item.createdAt
                        ? i18nDateToString(
                            stringToDate(item.createdAt, 'backend_datetime'),
                            'ui_short'
                          )
                        : '',
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'procurement.purchase_requests.settings.task_forms.columns.last_modified'
                  ),
                  render: (item: TaskFormItemFragment) => ({
                    type: 'text' as const,
                    value: {
                      text: item.updatedAt ? dateBackendDateTimeToRelativeTime(item.updatedAt) : '',
                    },
                  }),
                },
              ],
              frozenColumns: 1 as const,
            },
          },
        ]}
        emptyStates={{
          'no-data': {
            title: i18n.t('procurement.purchase_requests.settings.task_forms.empty_state_title'),
            description: i18n.t(
              'procurement.purchase_requests.settings.task_forms.empty_state_description'
            ),
          },
        }}
      />
    </>
  )
}

export default TaskFormsContent
